import { onReady } from '../../shared/utils/helpers';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import './plan.scss';
import GalleryViewer from '../../components/gallery-viewer/gallery-viewer';
import MobileGalleryViewer from '../../components/mobile-gallery-viewer/mobile-gallery-viewer';
import DownloadLink from '../../components/download-link/download-link';
import BuyerIntakeForm from '../../components/buyer-intake-form/buyer-intake-form';
import CombinatorPanel from '../../components/combinator-panel/combinator-panel';
import CombinatorModes from '../../components/combinator-panel/combinator-modes';
import {
  Recipe,
  buildConfig,
  planSelectRecipe,
  planSignatureRecipe,
  readPlans,
  withOutputs
} from '../../components/combinator-panel/factory';
import { Catalog, fetchCatalog } from '../../shared/utils/elevation-catalog';
import CollectionData from '../../components/collection-data/collection-data';

interface PlanPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class PlanPage extends Base {
  private header: Header;
  private specDownload: DownloadLink | null = null;
  private panels: CombinatorPanel[] = [];
  private modes: CombinatorModes[] = [];
  private collectionData: CollectionData | null = null;

  constructor(options: PlanPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;

    // Initialize header with same debug setting for consistent logging
    this.header = new Header({ debug: this.debug });

    this.init();
  }

  init(): void {
    this.log('PlanPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }
    try {
      this.collectionData = new CollectionData({ debug: this.debug });
      this.log('CollectionData initialized');
    } catch (e) {
      this.log('CollectionData init failed or skipped', e);
    }

    this.initCombinators();
    this.initGalleries();

    // The See More button carries this page's plan into the listings
    // filter; the slug is the path's last segment.
    const slug = window.location.pathname.split('/').filter(Boolean).pop();
    if (slug) {
      document
        .querySelectorAll<HTMLAnchorElement>('a[data-see-more-listings]')
        .forEach((link) => {
          link.href = `/listings?plan=${encodeURIComponent(slug)}`;
        });
    }

    // The spec sheet sits on Webflow's CDN, so the browser opens it rather than
    // saving it. This fetches it and hands it over as a download named after
    // the plan.
    try {
      this.specDownload = new DownloadLink({
        debug: this.debug,
        selector: 'a.kl-button--spec-dl',
        filenameFrom: '.profile-hero-heading',
        filenameSuffix: 'Spec Sheet'
      });
      this.log('DownloadLink initialized');
    } catch (e) {
      this.log('DownloadLink init failed or skipped', e);
    }

    // Initialize Buyer Intake Form. The container markup is the listing
    // template's, so the page states its own first-screen copy rather than
    // greeting a plan visitor with "Interested in This Property?".
    // Both containers on the page get the same pair.
    try {
      new BuyerIntakeForm({
        debug: this.debug,
        title: 'Interested in This Home Plan?',
        subtitle:
          'Provide a few details and we’ll follow up with pricing, available lots, and how to schedule a quick call.'
      });
      this.log('BuyerIntakeForm initialized');
    } catch (e) {
      this.log('BuyerIntakeForm init failed or skipped', e);
    }
  }

  /**
   * Both viewers count the grid's images at construction, and imginject
   * fills the grid after CollectionData resolves — so they wait for it.
   */
  private async initGalleries(): Promise<void> {
    if (this.collectionData) await this.collectionData.ready;

    try {
      new GalleryViewer({
        selector: '#launch-gallery',
        lightboxSelector: '.gallery-lightbox',
        contentSelector: '.gallery-lightbox__content',
        debug: this.debug
      });
    } catch (e) {
      this.log('GalleryViewer init failed or skipped', e);
    }

    // MobileGalleryViewer mounts on small screens only; a desktop viewport
    // keeps the desktop viewer alone.
    try {
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        // eslint-disable-next-line no-new
        new MobileGalleryViewer({ selector: '.gallery-lightbox__images', debug: this.debug });
      } else {
        this.log('MobileGalleryViewer skipped: viewport too large');
      }
    } catch (e) {
      this.log('MobileGalleryViewer init failed or skipped', e);
    }
  }

  /**
   * The plan of this page: `data-cmb-plan`, else the body's home-plan-base
   * item, else the URL's last segment.
   */
  private planSlugs(): string[] {
    const wrapper = document.querySelector<HTMLElement>('[data-cmb-plan]');
    const fromWrapper = readPlans(wrapper ? wrapper.getAttribute('data-cmb-plan') : null);

    if (fromWrapper.length) return fromWrapper;

    const body = document.body;
    const fromBody =
      body.getAttribute('data-cms-context') === 'home-plan-base' ? readPlans(body.getAttribute('data-cms-item')) : [];

    if (fromBody.length) return fromBody;

    return readPlans(window.location.pathname.split('/').filter(Boolean).pop() || '');
  }

  /** One catalog request feeds the pair and the single Select panel. */
  private async initCombinators(): Promise<void> {
    const mount = document.querySelector<HTMLElement>('[data-cmb-plan]');

    if (!mount) return;

    const plans = this.planSlugs();
    const catalog = await fetchCatalog({ debug: this.debug });

    if (!catalog || !plans.length) {
      this.log('CombinatorPanel: no catalog or no plan; the page keeps its markup', plans);
      return;
    }

    const copy = withOutputs({ catalog });
    const names = catalog.homePlans.filter((plan) => plans.includes(plan.slug)).map((plan) => plan.name);

    mount.querySelectorAll<HTMLElement>('[data-cmb-plan-name]').forEach((el) => {
      if (names.length) el.textContent = names.join(' and ');
    });

    this.modes = Array.from(document.querySelectorAll<HTMLElement>('[data-cmb-modes]')).map(
      (root) => new CombinatorModes({ root, debug: this.debug })
    );

    this.start(catalog, '#signature-config[data-cmb="signature"]', planSignatureRecipe(plans, copy));
    this.start(catalog, '#select-config[data-cmb="select"]', planSelectRecipe(plans, copy));
    // this.start(catalog, '#plan-combinator [data-cmb="select-single"]', planSelectRecipe(plans, copy));
  }

  private start(catalog: Catalog, selector: string, recipe: Recipe): void {
    const root = document.querySelector<HTMLElement>(selector);

    if (!root) return;

    const config = buildConfig(catalog, recipe);

    if (!config.outcomes.length) {
      this.log('CombinatorPanel: no outcomes for', selector);
      return;
    }

    this.panels.push(new CombinatorPanel({ root, config, debug: this.debug }));
  }

  destroy(): void {
    this.panels.forEach((panel) => panel.destroy());
    this.modes.forEach((modes) => modes.destroy());
  }
}

onReady(() => {
  // You can enable debug mode by passing { debug: true }
  new PlanPage({ debug: true });
});

export default PlanPage;
